import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { seoCannibalState, seoCannibalAction } from "@/lib/seo/seo-os.functions";
import { SeoPage, Stat, Panel, Pill, Loading, Empty, DataTable, num } from "@/components/admin/seo/SeoUi";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/seo/cannibalization")({
  head: () => ({ meta: [{ title: "Overlapping pages · Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: CannibalPage,
});

function CannibalPage() {
  const load = useServerFn(seoCannibalState);
  const act = useServerFn(seoCannibalAction);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["seo-cannibal"], queryFn: () => load() });
  const d = q.data as any;
  const rows = (d?.cannibalization ?? []) as any[];
  const experiments = (d?.experiments ?? []) as any[];

  const refresh = useMutation({
    mutationFn: () => act({ data: { action: "refresh" } }),
    onSuccess: (r: any) => {
      toast.success(r.hasData ? `${r.detected} overlapping searches found.` : "No search data available yet.");
      qc.invalidateQueries({ queryKey: ["seo-cannibal"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Refresh failed."),
  });

  const review = useMutation({
    mutationFn: (v: { id: string; status: "consolidate" | "accepted" }) => act({ data: { action: "review", ...v } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["seo-cannibal"] }),
  });

  const evaluate = useMutation({
    mutationFn: () => act({ data: { action: "evaluate_experiments" } }),
    onSuccess: (r: any) => {
      toast.success(`${r.evaluated} experiment(s) updated.`);
      qc.invalidateQueries({ queryKey: ["seo-cannibal"] });
    },
  });

  return (
    <SeoPage
      title="Overlapping pages & experiments"
      description="Searches where two or more of your own pages compete with each other, and the results of changes you've tested over 7, 28, 60 and 90 days."
      actions={
        <>
          <Button variant="outline" onClick={() => evaluate.mutate()} disabled={evaluate.isPending}>
            Update experiments
          </Button>
          <Button onClick={() => refresh.mutate()} disabled={refresh.isPending}>
            {refresh.isPending ? "Checking…" : "Check for overlaps"}
          </Button>
        </>
      }
    >
      {q.isLoading ? (
        <Loading />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Overlapping searches" value={num(rows.length)} />
            <Stat label="Serious overlaps" value={num(rows.filter((r) => r.severity === "critical").length)} />
            <Stat label="Experiments running" value={num(experiments.filter((e) => e.status === "running").length)} />
          </div>

          <Panel title="Overlapping searches">
            {rows.length === 0 ? (
              <Empty>No overlaps found. This needs Search Console data to work.</Empty>
            ) : (
              <DataTable
                head={["Search", "Competing pages", "Impressions", "Best performer", ""]}
                rows={rows.map((r) => [
                  r.query,
                  <Pill key={`${r.id}c`} tone={r.severity === "critical" ? "bad" : "warn"}>
                    {(r.pages ?? []).length} pages
                  </Pill>,
                  num(r.impressions ?? 0),
                  r.recommended_winner ?? "—",
                  <div key={`${r.id}a`} className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => review.mutate({ id: r.id, status: "consolidate" })}>
                      Merge
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => review.mutate({ id: r.id, status: "accepted" })}>
                      Fine as is
                    </Button>
                  </div>,
                ])}
              />
            )}
          </Panel>

          <Panel title="Experiments">
            {experiments.length === 0 ? (
              <Empty>No experiments started yet.</Empty>
            ) : (
              <DataTable
                head={["Change", "Page", "Status", "Checkpoints", "Outcome"]}
                rows={experiments.map((e) => [
                  e.title,
                  e.target_path,
                  e.status,
                  num((e.checkpoints ?? []).length),
                  e.outcome ?? "—",
                ])}
              />
            )}
          </Panel>
        </>
      )}
    </SeoPage>
  );
}
